import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { getStoredUser } from '@/hooks/useAuth';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import {
  GenerationForecastLatestResponse,
  GenerationForecastDayResponse,
  PvActualDayResponse,
  ForecastComparisonResponse,
  ForecastHalfHourResponse,
  getDayTabLabels,
  fmtDateTimeLocal,
  getMockDayForecast,
  getMockPvActualDay,
  getMockForecastComparison,
  getMockForecastHalfHour,
} from '@/mocks/generationForecast';
import ForecastKpi from './components/ForecastKpi';
import ForecastMainChart from './components/ForecastMainChart';
import CurtailmentBanner from './components/CurtailmentBanner';
import DeviationTable from './components/DeviationTable';
import ForecastExportModal from './components/ForecastExportModal';

// ASSET_ID now sourced dynamically from useSelectedAsset hook
const REFRESH_MS = 2 * 60 * 1000;

const formatNumber = (value: number | null | undefined, digits = 1, fallback = '\u2014'): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return fallback;
  return Number(value).toFixed(digits);
};


// --- Погодний чіп (Open-Meteo) для шапки сторінки прогнозу ---
type SkyMeta = { icon: string; color: string; label: string };
function pickSky(w: any): SkyMeta {
  if (!w) return { icon: 'ri-question-line', color: '#9B9BA4', label: '—' };
  const day = (w.shortwaveRadiation ?? 0) > 5;
  const cc = w.cloudCover ?? 0;
  const pr = w.precipitation ?? 0;
  if (pr >= 2.5) return { icon: 'ri-showers-line', color: '#60A5FA', label: 'Дощ' };
  if (pr >= 0.2) return { icon: 'ri-drizzle-line', color: '#7DD3FC', label: 'Мряка' };
  if (cc < 15) return day ? { icon: 'ri-sun-line', color: '#FBBF24', label: 'Ясно' } : { icon: 'ri-moon-clear-line', color: '#C7D2FE', label: 'Ясно' };
  if (cc < 50) return day ? { icon: 'ri-sun-cloudy-line', color: '#FCD34D', label: 'Мінлива хмарність' } : { icon: 'ri-moon-cloudy-line', color: '#A5B4FC', label: 'Мінлива хмарність' };
  if (cc < 85) return { icon: 'ri-cloudy-line', color: '#CBD5E1', label: 'Хмарно' };
  return { icon: 'ri-cloudy-2-line', color: '#94A3B8', label: 'Похмуро' };
}
function hhmm(v: any): string | null {
  if (!v || typeof v !== 'string') return null;
  const i = v.indexOf('T');
  const t = i >= 0 ? v.slice(i + 1) : v;
  return t.slice(0, 5) || null;
}
function WeatherChip({ weather, sun }: { weather: any; sun?: { sunrise?: string; sunset?: string } | null }) {
  const sky = pickSky(weather);
  const temp = weather?.temperature2m;
  const windMs = weather?.windSpeed10m != null ? weather.windSpeed10m / 3.6 : null;
  const sunrise = hhmm(sun?.sunrise);
  const sunset = hhmm(sun?.sunset);
  const sep = <span style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>;
  return (
    <div
      className="flex items-center gap-2 rounded-lg px-3 py-1.5"
      style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.08)' }}
      title={'Погода зараз (Open-Meteo) · ' + sky.label}
    >
      <i className={sky.icon + ' text-lg'} style={{ color: sky.color }}></i>
      <span className="text-xs" style={{ color: '#C7C7CC' }}>{sky.label}</span>
      <span style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
      <span className="text-sm font-semibold text-white">{temp != null ? Math.round(temp) + '°C' : '—'}</span>
      {sep}
      <i className="ri-windy-line text-sm" style={{ color: '#9B9BA4' }}></i>
      <span className="text-xs" style={{ color: '#A0A0A8' }}>{windMs != null ? windMs.toFixed(1) + ' м/с' : '—'}</span>
      {(sunrise || sunset) && sep}
      {sunrise && (
        <span className="flex items-center gap-1 text-xs" style={{ color: '#A0A0A8' }} title={'Схід сонця ' + sunrise}>
          <i className="ri-sun-line text-sm" style={{ color: '#FBBF24' }}></i>
          <i className="ri-arrow-up-s-line" style={{ color: '#6B6B70', marginLeft: -4, marginRight: -4 }}></i>
          {sunrise}
        </span>
      )}
      {sunset && (
        <span className="flex items-center gap-1 text-xs" style={{ color: '#A0A0A8' }} title={'Захід сонця ' + sunset}>
          <i className="ri-sun-line text-sm" style={{ color: '#FB923C' }}></i>
          <i className="ri-arrow-down-s-line" style={{ color: '#6B6B70', marginLeft: -4, marginRight: -4 }}></i>
          {sunset}
        </span>
      )}
    </div>
  );
}

// --- localStorage stale-while-revalidate cache ---
const CACHE_TTL_MS = 2 * 60 * 1000;
function readCache<T>(key: string): T | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('helios:' + key) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data as T;
  } catch { return null; }
}
function writeCache(key: string, data: unknown) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('helios:' + key, JSON.stringify({ ts: Date.now(), data }));
    }
  } catch { /* noop */ }
}

export default function ForecastPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = getStoredUser();

  const dayTabs = useMemo(() => getDayTabLabels(), []);
  const [selectedDate, setSelectedDate] = useState(() => {
    const todayTab = dayTabs.find((d) => d.label === 'Today');
    return todayTab?.apiDate ?? dayTabs[2]?.apiDate ?? '';
  });

  const [mode, setMode] = useState<'hourly' | '30min'>('30min');
  const [showExportModal, setShowExportModal] = useState(false);

  const [latest, setLatest] = useState<GenerationForecastLatestResponse | null>(null);
  const [dayData, setDayData] = useState<GenerationForecastDayResponse | null>(null);
  const [pvActualData, setPvActualData] = useState<PvActualDayResponse | null>(null);
  const [comparisonData, setComparisonData] = useState<ForecastComparisonResponse | null>(null);
  const [halfHourData, setHalfHourData] = useState<ForecastHalfHourResponse | null>(null);
  const [ecmwfShadowData, setEcmwfShadowData] = useState<{ hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null>(null);
  const [latestLoading, setLatestLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(true);
  const [pvLoading, setPvLoading] = useState(true);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [halfHourLoading, setHalfHourLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('—');
  const { selectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id ?? '';

  const fetchLatest = useCallback(async () => {
    if (!assetId) { setLatestLoading(false); return; }
    setLatestLoading(true);
    try {
      const result = await api.get<GenerationForecastLatestResponse>(
        `/forecasting/assets/${assetId}/generation-forecasts/latest?includeHourly=false`,
      );
      setLatest(result);
      setLastUpdated(fmtDateTimeLocal(result.forecastRunAt) ?? '—');
      setError(null);
    } catch (err) {
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) {
        setLatest(null);
      } else {
        setLatest(null);
        setLastUpdated('—');
        if (!isNet) setError(getApiErrorMessage(err, 'Failed to load forecast summary'));
      }
      if (isNet) setDemoMode(true);
    } finally {
      setLatestLoading(false);
    }
  }, [assetId]);

  const fetchDay = useCallback(async (date: string) => {
    if (!assetId) { setDayLoading(false); return; }
    const cacheKey = `generation-forecasts-day:${assetId}:${date}`;
    const cached = readCache<GenerationForecastDayResponse>(cacheKey);
    if (cached) { setDayData(cached); setDayLoading(false); }
    setDayLoading(true);
    try {
      const result = await api.get<GenerationForecastDayResponse>(
        `/forecasting/assets/${assetId}/generation-forecasts/day?date=${date}`,
      );
      setDayData(result);
      writeCache(cacheKey, result);
    } catch (err) {
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) {
        setDayData(null);
      } else {
        setDayData(null);
        if (!isNet) setError(getApiErrorMessage(err, 'Failed to load daily forecast'));
      }
      if (isNet) setDemoMode(true);
    } finally {
      setDayLoading(false);
    }
  }, [assetId]);

  const fetchPvActual = useCallback(async (date: string) => {
    if (!assetId) { setPvLoading(false); return; }
    const cacheKey = `pv-actuals-day:${assetId}:${date}`;
    const cached = readCache<PvActualDayResponse>(cacheKey);
    if (cached) { setPvActualData(cached); setPvLoading(false); }
    setPvLoading(true);
    try {
      const [result, hourlyResp] = await Promise.all([
        api.get<PvActualDayResponse>(
          `/forecasting/assets/${assetId}/pv-actuals/day?date=${date}&includeCurrent=true`,
        ),
        api.get<{ hourly: Array<{ hour: string; actualPacKw: number | null }> }>(
          `/forecasting/assets/${assetId}/hourly-actuals/day?date=${date}`,
        ).catch(() => ({ hourly: [] as Array<{ hour: string; actualPacKw: number | null }> })),
      ]);
      // Merge: for hours present in /hourly-actuals/day but missing in
      // /pv-actuals/day (strict H+30 filter rejected them), synthesize a row
      // so the green actual line on /forecast covers the hour.
      try {
        const haveHours = new Set<string>();
        for (const r of (result?.actual ?? [])) {
          if (r && (r as any).hour) haveHours.add((r as any).hour);
        }
        for (const h of (hourlyResp?.hourly ?? [])) {
          if (!h.hour || h.actualPacKw == null) continue;
          if (haveHours.has(h.hour)) continue;
          (result.actual as any[]).push({
            hour: h.hour,
            samplePosition: 'H+30',
            status: 'past',
            isFinal: true,
            actualPacKw: Number(h.actualPacKw),
            qualityFlag: 'hourly_avg_fallback',
          });
        }
        (result.actual as any[]).sort(
          (a: any, b: any) => String(a.hour || '').localeCompare(String(b.hour || '')),
        );
      } catch (e) {
        console.warn('[fetchPvActual] hourly-fallback merge failed:', e);
      }
      setPvActualData(result);
      writeCache(cacheKey, result);
    } catch (err) {
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) {
        setPvActualData(null);
      } else {
        setPvActualData(null);
        if (!isNet) setError(getApiErrorMessage(err, 'Failed to load PV actuals'));
      }
      if (isNet) setDemoMode(true);
    } finally {
      setPvLoading(false);
    }
  }, [assetId]);

  const fetchComparison = useCallback(async (date: string) => {
    if (!assetId) { setComparisonLoading(false); return; }
    const cacheKey = `forecast-comparison-day:${assetId}:${date}`;
    const cached = readCache<ForecastComparisonResponse>(cacheKey);
    if (cached) {
      setComparisonData(cached);
      setComparisonLoading(false); // already showing cached data — background refresh
    } else {
      setComparisonLoading(true);
    }
    try {
      const result = await api.get<ForecastComparisonResponse>(
        `/forecasting/assets/${assetId}/forecast-comparison/day?date=${date}`,
      );
      setComparisonData(result);
      writeCache(cacheKey, result);
    } catch (err) {
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) {
        setComparisonData(null);
      } else {
        setComparisonData(null);
        if (!isNet) setError(getApiErrorMessage(err, 'Failed to load forecast comparison'));
      }
      if (isNet) setDemoMode(true);
    } finally {
      setComparisonLoading(false);
    }
  }, [assetId]);

  const fetchHalfHour = useCallback(async (date: string) => {
    if (!assetId) { setHalfHourLoading(false); return; }
    const cacheKey = `forecast-comparison-half-hour:${assetId}:${date}`;
    const cached = readCache<ForecastHalfHourResponse>(cacheKey);
    if (cached) {
      setHalfHourData(cached);
      setHalfHourLoading(false);
    } else {
      setHalfHourLoading(true);
    }
    try {
      const result = await api.get<ForecastHalfHourResponse>(
        `/forecasting/assets/${assetId}/forecast-comparison/half-hour?date=${date}`,
      );
      setHalfHourData(result);
      writeCache(cacheKey, result);
    } catch (err) {
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) {
        setHalfHourData(null);
      } else {
        setHalfHourData(null);
        if (!isNet) setError(getApiErrorMessage(err, 'Failed to load 30-min forecast comparison'));
      }
      if (isNet) setDemoMode(true);
    } finally {
      setHalfHourLoading(false);
    }
  }, [assetId]);

  const fetchEcmwfShadow = useCallback(async (date: string) => {
    if (!assetId) return;
    try {
      const result = await api.get<{ hourly: Array<{ hour: string; forecastPowerKw: number | null }> }>(
        `/forecasting/assets/${assetId}/ecmwf-shadow/day?date=${date}`,
      );
      setEcmwfShadowData(result);
    } catch {
      setEcmwfShadowData(null);
    }
  }, [assetId]);

  // Initial load + 5-min auto refresh
  useEffect(() => {
    fetchLatest();
    fetchDay(selectedDate);
    fetchPvActual(selectedDate);
    fetchComparison(selectedDate);
    fetchHalfHour(selectedDate);
    fetchEcmwfShadow(selectedDate);
    const id = setInterval(() => {
      fetchLatest();
      fetchDay(selectedDate);
      fetchPvActual(selectedDate);
      fetchComparison(selectedDate);
      fetchHalfHour(selectedDate);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchLatest, fetchDay, fetchPvActual, fetchComparison, fetchHalfHour, selectedDate]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const selectedTab = dayTabs.find((d) => d.apiDate === selectedDate);
  const isAnyLoading = latestLoading || dayLoading || pvLoading || comparisonLoading || halfHourLoading;

  const nowWeather = useMemo(() => {
    const hourly = dayData?.hourly ?? [];
    if (!hourly.length) return null;
    const nowKey = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Kyiv' });
    const curHour = nowKey.slice(0, 2) + ':00';
    const match = hourly.find((h) => h.hour === curHour && h.weather);
    return match?.weather ?? [...hourly].reverse().find((h) => h.weather)?.weather ?? null;
  }, [dayData]);

  return (
    <DashboardLayout>
      <div className="min-h-screen -m-4" style={{ background: '#111113' }}>
        <div className="px-4 md:px-6 pt-6 pb-10 max-w-[1400px]">

          {/* Page header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bar-chart-2-line text-base" style={{ color: '#D12229' }}></i>
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('forecast_title')}
                  </h1>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded"
                    style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                  >
                    Онлайн
                  </span>
                </div>
                {nowWeather && <WeatherChip weather={nowWeather} sun={dayData?.sun} />}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Quick day selector */}
              <div
                className="flex items-center rounded-lg py-2 px-0.5"
                style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                {dayTabs.map((d) => (
                  <button
                    key={d.apiDate}
                    onClick={() => setSelectedDate(d.apiDate)}
                    className="px-3 py-1 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      background: selectedDate === d.apiDate ? '#D12229' : 'transparent',
                      color: selectedDate === d.apiDate ? '#fff' : '#A0A0A8',
                    }}
                  >
                    {d.label === 'Today' ? t('fc_today') : d.label}
                  </button>
                ))}
              </div>

              {/* Native date input */}
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm" style={{ color: '#6B6B70' }}></i>
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-transparent border-none outline-none text-sm cursor-pointer"
                  style={{ color: '#A0A0A8', fontFamily: 'Inter, sans-serif', colorScheme: 'dark' }}
                />
              </div>

              {/* Export XLSX button */}
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#A0A0A8',
                }}
                onClick={() => setShowExportModal(true)}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-download-line text-sm"></i>
                </div>
                {t('forecast_export')}
              </button>
            </div>
          </div>

          {/* Loading / Error states */}
          {isAnyLoading && (
            <div className="flex items-center gap-3 mb-6">
              <i className="ri-loader-4-line animate-spin text-lg" style={{ color: '#6B6B70' }}></i>
              <span className="text-sm" style={{ color: '#6B6B70' }}>{t('fc_loading_data')}</span>
            </div>
          )}

          {error && !isAnyLoading && (
            <div
              className="rounded-xl p-4 mb-6 flex items-center gap-3"
              style={{ background: 'rgba(209,34,41,0.08)', border: '1px solid rgba(209,34,41,0.2)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-error-warning-line text-lg" style={{ color: '#D12229' }}></i>
              </div>
              <div>
                <div className="text-sm font-medium text-white">{t('fc_load_error_title')}</div>
                <div className="text-xs" style={{ color: '#6B6B70' }}>{error}</div>
              </div>
            </div>
          )}

          {demoMode && !error && !isAnyLoading && (
            <div
              className="rounded-xl p-3 mb-6 flex items-center gap-2"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-wifi-off-line text-sm" style={{ color: '#6B6B70' }}></i>
              </div>
              <div className="text-xs" style={{ color: '#6B6B70' }}>{t('fc_demo_banner')}</div>
            </div>
          )}

          {/* KPI row */}
          <div className="mb-5">
            <ForecastKpi latest={latest} loading={latestLoading} pvActualData={pvActualData} ecmwfShadowData={ecmwfShadowData} comparisonData={comparisonData} dayData={dayData} />
          </div>

          {/* Main chart */}
          <div className="mb-5">
            <CurtailmentBanner active={halfHourData?.activeCurtailment} assetName={selectedAsset?.assetName} />
            <ForecastMainChart
              comparisonData={comparisonData}
              halfHourData={halfHourData}
              ecmwfShadowData={ecmwfShadowData}
              pvActualData={pvActualData}
              mode={mode}
              dayLabel={selectedTab ? (selectedTab.label === 'Today' ? t('fc_today') : selectedTab.label) : selectedDate}
              dayDisplay={selectedTab?.displayDate ?? selectedDate}
              loading={mode === 'hourly' ? comparisonLoading : halfHourLoading}
              onModeChange={setMode}
            />
          </div>

          {/* Deviation table full width */}
          <div className="mb-5">
            <DeviationTable
              dayData={dayData}
              pvActualData={pvActualData}
              dayLabel={selectedTab ? (selectedTab.label === 'Today' ? t('fc_today') : selectedTab.label) : selectedDate}
              loading={dayLoading || pvLoading}
              onRefresh={() => { fetchLatest(); fetchDay(selectedDate); fetchPvActual(selectedDate); fetchComparison(selectedDate); fetchHalfHour(selectedDate); }}
            />
          </div>

          {/* Footer */}
          <div className="mt-6 text-xs" style={{ color: '#4A4A50' }}>
            {t('fc_footer_updated')}: {lastUpdated} · {t('fc_footer_model')}: {latest?.modelVersion ?? '—'} · {t('fc_footer_refresh')}
          </div>
        </div>
      </div>

      {/* Export modal */}
      {showExportModal && (
        <ForecastExportModal
          assetId={assetId}
          defaultDateFrom={selectedDate}
          defaultDateTo={selectedDate}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </DashboardLayout>
  );
}